import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Asset } from '../types';
import { assetService } from '../services';
import { dataSourceService, SecuritySearchResult } from '../services/dataSourceService';
import { getErrorStatus } from '../utils/errors';
import { useToast, useThrottle } from '../hooks';
import { useZodForm } from '../hooks';
import { assetSchema, AssetFormData as AssetZodFormData } from '../schemas';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import EmptyState from '../components/ui/EmptyState';
import PageTopBar from '../components/ui/PageTopBar';
import SearchBar from '../components/ui/SearchBar';
import Select from '../components/ui/Select';
import SortHeader from '../components/ui/SortHeader';
import ConfirmationModal from '../components/common/ConfirmationModal';
import QuickStartModal from '../components/assignments/QuickStartModal';

// Icons
const CloseIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const typeVariants: Record<string, 'primary' | 'secondary' | 'success' | 'warning'> = {
  company: 'primary',
  fund: 'success',
  property: 'warning',
  other: 'secondary',
};

const Coverage: React.FC = () => {
  const toast = useToast();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isQuickStartOpen, setIsQuickStartOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Table filter state
  const [filterQuery, setFilterQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterSector, setFilterSector] = useState('');

  // Sort state
  type AssetSortField = 'name' | 'type' | 'sector' | 'uid';
  type SortDir = 'asc' | 'desc';
  const [sortField, setSortField] = useState<AssetSortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const toggleSort = (field: AssetSortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Search state (UI-only, not part of form)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SecuritySearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [providerDescription, setProviderDescription] = useState('');
  const [isFromProvider, setIsFromProvider] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const suppressDropdownRef = useRef(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useZodForm<AssetZodFormData>(assetSchema, {
    defaultValues: { name: '', description: '', dataUid: '', type: 'company', sector: '' },
    mode: 'onBlur',
  });

  const formDataUid = watch('dataUid');
  const formType = watch('type');
  const formSector = watch('sector');

  const throttledSearchQuery = useThrottle(searchQuery);

  // Search securities when query changes
  useEffect(() => {
    const search = async () => {
      if (throttledSearchQuery.length < 1) {
        setSearchResults([]);
        setSearchError(null);
        return;
      }

      setIsSearching(true);
      setSearchError(null);
      try {
        const results = await dataSourceService.searchSecurities(throttledSearchQuery, 10);
        setSearchResults(results);

        // Auto-pick when the typed text exactly matches a uid in the results.
        // Saves users from having to click the dropdown when they typed the
        // symbol directly (e.g. "AAPL" or "aapl" -> Apple Inc.). Skips when
        // we already have a provider-selected asset to avoid re-firing on
        // the post-select round where searchQuery becomes the security name.
        const typed = throttledSearchQuery.trim().toUpperCase();
        const exactMatch = results.find(r => r.uid.toUpperCase() === typed);
        if (exactMatch && !isFromProvider) {
          handleSelectSecurity(exactMatch);
          return;
        }

        if (suppressDropdownRef.current) {
          suppressDropdownRef.current = false;
        } else {
          setShowDropdown(true);
        }
      } catch (err: unknown) {
        console.error('Search failed:', err);
        setSearchResults([]);
        const status = getErrorStatus(err);
        const msg =
          status === 503 ? 'Securities data is temporarily unavailable.'
          : status === 401 ? 'Session expired — please refresh and log in again.'
          : 'Search failed. Try again in a moment.';
        setSearchError(msg);
      } finally {
        setIsSearching(false);
      }
    };

    search();
  }, [throttledSearchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectSecurity = (security: SecuritySearchResult) => {
    setProviderDescription(security.description);
    setIsFromProvider(true);
    suppressDropdownRef.current = true;
    setSearchQuery(security.name);
    setShowDropdown(false);

    // Update form values — shouldValidate on name+dataUid to update isValid
    setValue('name', security.name, { shouldDirty: true, shouldValidate: true });
    setValue('dataUid', security.uid, { shouldDirty: true, shouldValidate: true });
    setValue('type', security.type.toLowerCase() as AssetZodFormData['type'], {
      shouldDirty: true,
    });
    setValue('sector', security.sector, { shouldDirty: true });
  };

  const handleNameChange = (value: string) => {
    setSearchQuery(value);
    setValue('name', value, { shouldDirty: true });

    // Any manual typing invalidates the provider selection
    if (isFromProvider) {
      setIsFromProvider(false);
      setProviderDescription('');
      setValue('dataUid', '', { shouldDirty: true, shouldValidate: true });
      setValue('type', 'company', { shouldDirty: true });
      setValue('sector', '', { shouldDirty: true });
    }
  };

  const loadAssets = async () => {
    try {
      setLoading(true);
      const response = await assetService.getAssets(1, 100);
      setAssets(response.data);
    } catch (err) {
      console.error('Failed to load assets:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssets();
  }, []);

  const openCreateModal = () => {
    setEditingAsset(null);
    reset({ name: '', description: '', dataUid: '', type: 'company', sector: '' });
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
    setProviderDescription('');
    setIsFromProvider(false);
    setIsModalOpen(true);
  };

  const openEditModal = async (asset: Asset) => {
    setEditingAsset(asset);
    suppressDropdownRef.current = true;
    setSearchQuery(asset.name);
    setSearchResults([]);
    setShowDropdown(false);
    setProviderDescription('');
    setIsFromProvider(false);
    setIsModalOpen(true);

    reset({
      name: asset.name,
      description: asset.notes || '',
      dataUid: asset.dataUid || '',
      type: asset.type,
      sector: asset.sector || '',
    });

    if (asset.dataUid) {
      try {
        const results = await dataSourceService.searchSecurities(asset.dataUid, 1);
        const match = results.find(r => r.uid === asset.dataUid);
        if (match) {
          setProviderDescription(match.description);
          setIsFromProvider(true);
        }
      } catch (err) {
        console.error('Failed to fetch security description:', err);
      }
    }
  };

  const onSubmit = async (data: AssetZodFormData) => {
    try {
      const payload = {
        name: data.name.trim(),
        dataUid: data.dataUid?.trim() || undefined,
        type: data.type,
        sector: data.sector?.trim() || undefined,
        notes: data.description?.trim() || undefined,
      };
      if (editingAsset) {
        await assetService.updateAsset(editingAsset.id, payload);
      } else {
        await assetService.createAsset(payload);
      }

      await loadAssets();
      setIsModalOpen(false);
    } catch (err) {
      console.error('Failed to save asset:', err);
      toast.error('Failed to save asset. Please try again.');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await assetService.deleteAsset(id);
      await loadAssets();
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete asset:', err);
      toast.error('Failed to delete asset. Please try again.');
    }
  };

  // Derive unique types and sectors for filter dropdowns
  const uniqueTypes = useMemo(() => Array.from(new Set(assets.map(a => a.type))).sort(), [assets]);
  const uniqueSectors = useMemo(() => {
    const sectors = assets.map(a => a.sector).filter(Boolean) as string[];
    return Array.from(new Set(sectors)).sort();
  }, [assets]);

  // Filter + sort
  const filteredSortedAssets = useMemo(() => {
    let result = assets;

    // Filter
    if (filterQuery) {
      const q = filterQuery.toLowerCase();
      result = result.filter(a =>
        a.name.toLowerCase().includes(q) ||
        (a.dataUid && a.dataUid.toLowerCase().includes(q))
      );
    }
    if (filterType) {
      result = result.filter(a => a.type === filterType);
    }
    if (filterSector) {
      result = result.filter(a => a.sector === filterSector);
    }

    // Sort
    const sorted = [...result];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'type':
          cmp = a.type.localeCompare(b.type);
          break;
        case 'sector':
          cmp = (a.sector || '').localeCompare(b.sector || '');
          break;
        case 'uid':
          cmp = (a.dataUid || '').localeCompare(b.dataUid || '');
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [assets, filterQuery, filterType, filterSector, sortField, sortDir]);

  const hasActiveFilters = filterQuery || filterType || filterSector;

  if (loading) {
    return (
      <div className="h-full bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-neutral-50">
      <PageTopBar
        title="Coverage"
        description="Manage companies and assets you cover for workflow assignments and data lookups"
      />
      <div className="flex-1 overflow-y-auto">
      <div className="w-full px-6 lg:px-8 py-8">

        {/* Filters */}
        {assets.length > 0 && (
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <SearchBar
              value={filterQuery}
              onChange={setFilterQuery}
              placeholder="Search by name or UID..."
            />
            <Select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              options={uniqueTypes.map(t => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))}
              placeholder="All types"
              className="w-40"
            />
            <Select
              value={filterSector}
              onChange={e => setFilterSector(e.target.value)}
              options={uniqueSectors.map(s => ({ value: s, label: s }))}
              placeholder="All sectors"
              className="w-44"
            />
            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={() => { setFilterQuery(''); setFilterType(''); setFilterSector(''); }}>
                Clear all
              </Button>
            )}

            {/* Primary actions — pushed to the right */}
            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setIsQuickStartOpen(true)}>
                Start Workup
              </Button>
              <Button variant="primary" size="sm" onClick={openCreateModal}>
                Add Coverage
              </Button>
            </div>
          </div>
        )}

        {assets.length === 0 ? (
          <EmptyState
            title="No coverage yet"
            description="Add companies or assets you cover to assign workflows and pull external data."
            action={{
              label: 'Add Coverage',
              onClick: openCreateModal,
            }}
          />
        ) : (
          <Card variant="default">
            <div className="overflow-x-auto no-scrollbar">
              <table className="w-full">
                <thead>
                  <tr className="bg-neutral-50 border-y border-neutral-100">
                    <SortHeader label="Name" active={sortField === 'name'} dir={sortDir} onSort={() => toggleSort('name')} />
                    <SortHeader label="Type" active={sortField === 'type'} dir={sortDir} onSort={() => toggleSort('type')} />
                    <SortHeader label="Sector" active={sortField === 'sector'} dir={sortDir} onSort={() => toggleSort('sector')} />
                    <SortHeader label="UID" active={sortField === 'uid'} dir={sortDir} onSort={() => toggleSort('uid')} />
                    <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {filteredSortedAssets.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-sm text-neutral-500">
                        No assets match your filters
                      </td>
                    </tr>
                  ) : filteredSortedAssets.map(asset => (
                    <tr key={asset.id} className="hover:bg-neutral-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-neutral-900">{asset.name}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={typeVariants[asset.type] || 'secondary'} size="sm">
                          {asset.type}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                        {asset.sector || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {asset.dataUid ? (
                          <code className="text-xs font-mono text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded">
                            {asset.dataUid}
                          </code>
                        ) : (
                          <span className="text-sm text-neutral-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditModal(asset)}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded shadow-sm hover:bg-primary-700 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(asset.id)}
                            className="px-3 py-1.5 text-xs font-medium text-danger-600 border border-danger-300 rounded shadow-sm hover:bg-danger-50 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* min-h-[125dvh] (= 100dvh ÷ 0.8 body zoom) so the centering box fills the
              viewport under zoom and a tall modal doesn't clip. */}
          <div className="flex items-center justify-center min-h-[125dvh] px-4">
            <div
              className="fixed inset-0 bg-black/70 transition-opacity"
              onClick={() => setIsModalOpen(false)}
            />
            <div className="relative bg-surface rounded-xl shadow-2xl border border-neutral-200 max-w-md w-full mx-auto z-10">
              <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-neutral-900">
                  {editingAsset ? 'Edit Coverage' : 'Add New Coverage'}
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-neutral-400 hover:text-neutral-600 transition-colors"
                >
                  <CloseIcon />
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
                {/* Name Search Field */}
                <div className="relative">
                  <Input
                    ref={searchInputRef}
                    label="Name"
                    required
                    value={searchQuery}
                    onChange={e => handleNameChange(e.target.value)}
                    onBlur={() => trigger(['name', 'dataUid'])}
                    placeholder="Start typing to search..."
                    error={!!(errors.name || errors.dataUid || searchError)}
                    errorMessage={errors.name?.message || errors.dataUid?.message || searchError || undefined}
                    rightIcon={
                      isSearching ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-600 border-t-transparent" />
                      ) : undefined
                    }
                  />

                  {/* Search Dropdown */}
                  {showDropdown && searchResults.length > 0 && (
                    <div
                      ref={dropdownRef}
                      className="absolute z-20 w-full mt-1 bg-surface border border-neutral-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    >
                      {searchResults.map(security => (
                        <button
                          key={security.uid}
                          type="button"
                          onClick={() => handleSelectSecurity(security)}
                          className="w-full px-3 py-2.5 text-left hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-medium text-neutral-900">{security.name}</span>
                              <span className="ml-2 text-xs text-neutral-500">{security.type}</span>
                            </div>
                            <code className="text-xs font-mono text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded">
                              {security.uid}
                            </code>
                          </div>
                          <p className="text-xs text-neutral-500 mt-0.5 truncate">
                            {security.description}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* UID and Provider Description */}
                {formDataUid && (
                  <div className="px-3 py-2.5 bg-neutral-50 rounded-lg border border-neutral-200 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-neutral-600">UID:</span>
                      <code className="text-sm font-mono font-medium text-primary-700">
                        {formDataUid}
                      </code>
                    </div>
                    {providerDescription && (
                      <p className="text-sm text-neutral-600">{providerDescription}</p>
                    )}
                  </div>
                )}

                {/* User Notes */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1.5">Notes</label>
                  <textarea
                    {...register('description')}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg transition-all resize-none"
                    placeholder="Add your own notes about this coverage"
                  />
                </div>

                {/* Type and Sector */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                      Type
                    </label>
                    {isFromProvider || editingAsset ? (
                      <div className="w-full h-10 px-3 text-sm border border-neutral-200 rounded-lg bg-neutral-50 flex items-center text-neutral-600 capitalize">
                        {formType}
                      </div>
                    ) : (
                      <select
                        {...register('type')}
                        className="w-full h-10 px-3 text-sm border border-neutral-200 rounded-lg transition-all bg-surface"
                      >
                        <option value="company">Company</option>
                        <option value="fund">Fund</option>
                        <option value="property">Property</option>
                        <option value="other">Other</option>
                      </select>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                      Sector
                    </label>
                    {isFromProvider || editingAsset ? (
                      <div className="w-full h-10 px-3 text-sm border border-neutral-200 rounded-lg bg-neutral-50 flex items-center text-neutral-600">
                        {formSector || '-'}
                      </div>
                    ) : (
                      <input
                        type="text"
                        {...register('sector')}
                        className="w-full h-10 px-3 text-sm border border-neutral-200 rounded-lg transition-all"
                        placeholder="e.g., Technology"
                      />
                    )}
                  </div>
                </div>

                <div className="flex justify-between pt-4 border-t border-neutral-200">
                  <div>
                    {editingAsset && (
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        onClick={() => setDeleteConfirm(editingAsset.id)}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" size="sm" onClick={() => setIsModalOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      size="sm"
                      disabled={isSubmitting || !isValid || !isDirty}
                    >
                      {isSubmitting ? 'Saving...' : editingAsset ? 'Save Changes' : 'Add Coverage'}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      <QuickStartModal isOpen={isQuickStartOpen} onClose={() => setIsQuickStartOpen(false)} />
      <ConfirmationModal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onAction={async () => {
          if (!deleteConfirm) return;
          await handleDelete(deleteConfirm);
          setIsModalOpen(false);
        }}
        title="Delete Coverage"
        description="Are you sure you want to delete this coverage? This action cannot be undone."
        actionText="Delete"
      />
    </div>
  );
};

export default Coverage;
